import { useRef, useCallback } from "react";
import { cn } from "@repo/utils";
import { MetaFieldRow } from "../field/meta-field-row";
import {
  useBeforeUnloadWarning,
  useClickInterception,
  useUnsavedChangesWarning,
} from "../hooks/use-unsaved-changes-warning";
import { ItemPendingChangesActions } from "../item-pending-changes-actions";
import type { Item, FieldData } from "../types";
import type { UseFieldMutations, UseTasksInfo } from "./types";
import { useItemFieldEditing } from "./use-item-field-editing";

type Props = {
  itemFields: FieldData[];
  items: Item[];
  apiBaseUrl: string;
  isDark?: boolean;
  windowId?: string;
  useFieldMutations: UseFieldMutations;
  useTasksInfo: UseTasksInfo;
};

export const ItemMetaTable = ({
  itemFields,
  items,
  apiBaseUrl,
  isDark = false,
  windowId,
  useFieldMutations,
  useTasksInfo,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    editingField,
    editValues,
    pendingChanges,
    isSaving,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleInputChange,
    handleSaveAllChanges,
    discardAllChanges,
    handleClearClick,
  } = useItemFieldEditing({ items, itemFields, windowId, useFieldMutations, useTasksInfo });

  const { showDiscardDialog } = useUnsavedChangesWarning({
    pendingChanges,
    onDiscard: discardAllChanges,
  });

  const handleCancelAllClick = useCallback(() => {
    showDiscardDialog();
  }, [showDiscardDialog]);

  useClickInterception(containerRef, pendingChanges, showDiscardDialog);
  useBeforeUnloadWarning(pendingChanges);

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div ref={containerRef} className="flex h-full flex-col">
      <table className="w-full table-fixed border-collapse">
        <thead className={cn("sticky top-0", { "bg-white": !isDark, "bg-white/50": isDark })}>
          <tr className="border-b border-gray-200">
            <th className="w-25 pt-3 pr-4 pb-2 pl-1 text-left text-[12px] font-semibold text-gray-700">
              Field
            </th>
            <th className="px-4 pt-3 pb-2 text-left text-[12px] font-semibold text-gray-700">
              Value
            </th>
          </tr>
        </thead>
        <tbody
          className={cn("divide-y divide-gray-100", { "bg-white": !isDark, "bg-white/80": isDark })}
        >
          {itemFields.map((field) => {
            const isEditing = editingField === String(field.id);
            const hasPendingChange = !!pendingChanges[field.id];
            const displayValue = hasPendingChange
              ? pendingChanges[field.id]!.newValue
              : field.isMixed
                ? ""
                : (field.values[0] ?? "");
            const editValue = editValues[field.id] ?? displayValue;

            return (
              <MetaFieldRow
                key={field.id}
                field={field}
                isEditing={isEditing}
                hasPendingChange={hasPendingChange}
                displayValue={displayValue}
                editValue={editValue}
                apiBaseUrl={apiBaseUrl}
                isMixed={field.isMixed && !hasPendingChange}
                onEditClick={handleEditClick}
                onValueChange={handleInputChange}
                onSave={handleSaveClick}
                onCancel={handleCancelClick}
                onClearClick={handleClearClick}
              />
            );
          })}
        </tbody>
      </table>

      {hasPendingChanges && (
        <ItemPendingChangesActions
          pendingChangesCount={Object.keys(pendingChanges).length}
          onCancelAllClick={handleCancelAllClick}
          onSaveAllChanges={handleSaveAllChanges}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};
